import React, { createContext, useContext, useMemo, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  MyWorkplace,
  setLastWorkplaceId,
  useMyWorkplaces,
} from "../hooks/useMyWorkplaces";

const sidebarWidth = "16rem";

const ShellRoot = styled.div<{ $desktopSidebarOpen: boolean }>`
  min-height: 100vh;
  background: #f4f5f7;

  @media (min-width: 768px) {
    display: flex;
  }
`;

const Sidebar = styled.aside<{ $desktopOpen: boolean }>`
  display: none;

  @media (min-width: 768px) {
    display: ${({ $desktopOpen }) => ($desktopOpen ? "flex" : "none")};
    width: ${sidebarWidth};
    height: 100vh;
    flex: 0 0 ${sidebarWidth};
    flex-direction: column;
    border-right: 1px solid #dfe1e6;
    background: #ffffff;
  }
`;

const Main = styled.main`
  min-width: 0;
  height: 100vh;

  @media (min-width: 768px) {
    flex: 1;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
  padding: 1rem;
`;

const BrandInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
  flex: 1;
`;

const BrandMark = styled.img`
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
`;

const BrandText = styled.div`
  min-width: 0;
`;

const BrandName = styled.div`
  color: #172b4d;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BrandSubtext = styled.div`
  color: #5e6c84;
  font-size: 0.75rem;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CloseButton = styled.button`
  width: 2rem;
  height: 2rem;
  border: 1px solid #dfe1e6;
  border-radius: 4px;
  background: #ffffff;
  color: #172b4d;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1;
  padding: 0;

  &:hover {
    background: #ebecf0;
  }
`;

const NavSection = styled.nav`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.5rem;
`;

const SectionLabel = styled.div`
  padding: 0.5rem 0.5rem 0.375rem;
  color: #5e6c84;
  font-size: 0.6875rem;
  font-weight: 800;
`;

const WorkplaceButton = styled.button<{ $active: boolean }>`
  width: 100%;
  min-height: 3.75rem;
  border: 1px solid ${({ $active }) => ($active ? "#36b37e" : "transparent")};
  border-radius: 6px;
  background: ${({ $active }) => ($active ? "#e3fcef" : "transparent")};
  color: #172b4d;
  cursor: pointer;
  padding: 0.625rem 0.75rem;
  text-align: left;

  &:hover {
    background: ${({ $active }) => ($active ? "#e3fcef" : "#f4f5f7")};
  }
`;

const WorkplaceName = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WorkplaceMeta = styled.div`
  margin-top: 0.25rem;
  color: #5e6c84;
  font-size: 0.875rem;
  line-height: 1.55;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Footer = styled.div`
  border-top: 1px solid #dfe1e6;
  padding: 0.75rem;
`;

const SignOutButton = styled.button`
  width: 100%;
  min-height: 2.5rem;
  border: 1px solid #dfe1e6;
  border-radius: 4px;
  background: #ffffff;
  color: #172b4d;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1;
  padding: 0 0.75rem;

  &:hover {
    background: #ebecf0;
  }
`;

const DrawerBackdrop = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? "block" : "none")};
  position: fixed;
  inset: 0;
  z-index: 20;
  background: rgba(9, 30, 66, 0.48);

  @media (min-width: 768px) {
    display: none;
  }
`;

const Drawer = styled.aside<{ $open: boolean }>`
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 21;
  display: flex;
  width: min(82vw, ${sidebarWidth});
  flex-direction: column;
  background: #ffffff;
  transform: translateX(${({ $open }) => ($open ? "0" : "-100%")});
  transition: transform 0.18s ease;

  @media (min-width: 768px) {
    display: none;
  }
`;

const EmptyText = styled.div`
  padding: 0.75rem;
  color: #5e6c84;
  font-size: 0.8125rem;
  line-height: 1.5;
`;

function currentWorkplaceIdFromPath(pathname: string) {
  const match = pathname.match(/^\/workplaces\/([^/]+)/);
  return match?.[1] ?? null;
}

type AppShellContextValue = {
  openMobileNavigation: () => void;
  openDesktopNavigation: () => void;
  desktopSidebarOpen: boolean;
};

const AppShellContext = createContext<AppShellContextValue | null>(null);

export function useAppShell() {
  return useContext(AppShellContext);
}

function SidebarContent({
  currentWorkplaceId,
  onClose,
  onSelectWorkplace,
  onSignOut,
  workplaces,
}: {
  currentWorkplaceId: string | null;
  onClose: () => void;
  onSelectWorkplace: (workplace: MyWorkplace) => void;
  onSignOut: () => void;
  workplaces: MyWorkplace[];
}) {
  return (
    <>
      <Brand>
        <BrandInfo>
          <BrandMark src="/app-icon.jpg" alt="" aria-hidden="true" />
          <BrandText>
            <BrandName>Shiftas</BrandName>
            <BrandSubtext>シフト共有</BrandSubtext>
          </BrandText>
        </BrandInfo>
        <CloseButton
          type="button"
          aria-label="ナビゲーションを閉じる"
          title="ナビゲーションを閉じる"
          onClick={onClose}
        >
          ×
        </CloseButton>
      </Brand>
      <NavSection aria-label="職場">
        <SectionLabel>WORKPLACES</SectionLabel>
        {workplaces.length === 0 ? (
          <EmptyText>所属している職場がありません。</EmptyText>
        ) : (
          workplaces.map((workplace) => (
            <WorkplaceButton
              key={workplace.workplaceUserId}
              type="button"
              $active={workplace.workplaceId === currentWorkplaceId}
              onClick={() => onSelectWorkplace(workplace)}
            >
              <WorkplaceName>{workplace.workplace.name}</WorkplaceName>
              <WorkplaceMeta>{workplace.name}</WorkplaceMeta>
            </WorkplaceButton>
          ))
        )}
      </NavSection>
      <Footer>
        <SignOutButton type="button" onClick={onSignOut}>
          ログアウト
        </SignOutButton>
      </Footer>
    </>
  );
}

export const AppShell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { workplaces } = useMyWorkplaces();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);

  const currentWorkplaceId = useMemo(
    () => currentWorkplaceIdFromPath(location.pathname),
    [location.pathname]
  );

  // 既存ユーザーが /workplaces/:workplaceId/shifts へ直アクセスする動線を維持する。
  // 職場一覧に一致する職場があればハイライトするが、一覧の読み込み中や未一致だけを理由に
  // 開いているURLから別ページへリダイレクトしない。
  const handleSelectWorkplace = (workplace: MyWorkplace) => {
    setLastWorkplaceId(workplace.workplaceId);
    setMobileOpen(false);
    navigate(`/workplaces/${workplace.workplaceId}/shifts${location.search}`);
  };

  return (
    <AppShellContext.Provider
      value={{
        openMobileNavigation: () => setMobileOpen(true),
        openDesktopNavigation: () => setDesktopSidebarOpen(true),
        desktopSidebarOpen,
      }}
    >
      <ShellRoot $desktopSidebarOpen={desktopSidebarOpen}>
        <Sidebar $desktopOpen={desktopSidebarOpen}>
          <SidebarContent
            currentWorkplaceId={currentWorkplaceId}
            workplaces={workplaces}
            onClose={() => setDesktopSidebarOpen(false)}
            onSelectWorkplace={handleSelectWorkplace}
            onSignOut={auth.signOut}
          />
        </Sidebar>
        <DrawerBackdrop $open={mobileOpen} onClick={() => setMobileOpen(false)} />
        <Drawer $open={mobileOpen}>
          <SidebarContent
            currentWorkplaceId={currentWorkplaceId}
            workplaces={workplaces}
            onClose={() => setMobileOpen(false)}
            onSelectWorkplace={handleSelectWorkplace}
            onSignOut={auth.signOut}
          />
        </Drawer>
        <Main>{children}</Main>
      </ShellRoot>
    </AppShellContext.Provider>
  );
};
